import { useEffect, useState } from 'react';

import Analytics from '@hh.ru/analytics-js';
import { Badge, Text, HSpacingContainer, useBreakpoint } from '@hh.ru/magritte-ui';
import { AdjustmentsOutlinedSize24 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import throttle from 'bloko/common/throttle';

import FloatingButton from 'src/components/FloatingButton';
import { useDraftClustersLogic } from 'src/components/Search/Common/Filters/hooks/draft/useDraftClustersLogic';
import { useInitDraftClusters } from 'src/components/Search/Common/Filters/hooks/draft/useInitDraftClusters';
import translation from 'src/components/translation';
import { useSearchSettings } from 'src/hooks/search/useSearchSettings';
import { useTotalVacancyFiltersCount } from 'src/hooks/search/useTotalVacancyFiltersCount';
import { useSelector } from 'src/hooks/useSelector';

import { useToggleSearchPreference } from 'src/components/SearchSorts/hooks/useToggleSearchPreference';

const SCROLL_THROTTLE_DELAY_MS = 100;

const TrlKeys = {
    buttonFilter: 'search.vacancy.button.filters.s',
    buttonFilterXS: 'novafilters.similarSavedSearch.filterXS',
};

const SearchPreferenceButton: TranslatedComponent = ({ trls }) => {
    const showSearchPreference = useSelector((state) => state.searchPreference.isShown);

    const toggleSearchPreference = useToggleSearchPreference();
    const { searchType, isResumeSearchType } = useSearchSettings();
    const totalUsedVacancyFilters = useTotalVacancyFiltersCount();
    const isDraftClustersLogic = useDraftClustersLogic();
    const initDraftClusters = useInitDraftClusters();

    const totalUsedResumeFilters = useSelector((state) => state.resumeSearchResult.totalUsedFilters) || 0;

    const totalUsedFilters = isResumeSearchType ? totalUsedResumeFilters : totalUsedVacancyFilters;

    const [isHiddenLabel, setIsHiddenLabel] = useState(false);

    const { isMobile } = useBreakpoint();

    useEffect(() => {
        if (!isMobile) {
            return undefined;
        }

        const checkScroll = throttle(() => {
            setIsHiddenLabel(window.scrollY > 0);
        }, SCROLL_THROTTLE_DELAY_MS);

        window.addEventListener('scroll', checkScroll);

        return () => window.removeEventListener('scroll', checkScroll);
    }, [isMobile]);

    if (!isMobile) {
        return null;
    }

    return (
        <FloatingButton
            onClick={() => {
                Analytics.sendHHEventButtonClick(`${searchType}_search_filter_open`);
                toggleSearchPreference(!showSearchPreference);
                if (isDraftClustersLogic) {
                    initDraftClusters();
                }
            }}
        >
            <HSpacingContainer default={12}>
                <AdjustmentsOutlinedSize24 initialColor="contrast" />
                {!isHiddenLabel && (
                    <Text Element="span" typography="subtitle-1-semibold" style="contrast">
                        {trls[TrlKeys.buttonFilter]}
                    </Text>
                )}
                {totalUsedFilters > 0 && <Badge>{`${totalUsedFilters}`}</Badge>}
            </HSpacingContainer>
        </FloatingButton>
    );
};

export default translation(SearchPreferenceButton);
